import * as React from 'react';
import { createPortal } from 'react-dom';
import { WithCommonProps } from '@types';
import { usePopout, useClickAway, PopoutPlacement } from '@hooks';
import { twMerge } from 'tailwind-merge';

// UI
import {
  tooltipContentStylesAsClassName,
  tooltipStylesAsClassName,
} from './tooltip-styles';

export enum TooltipPosition {
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
}

export enum TooltipAlignment {
  START = 'start',
  CENTER = 'center',
  END = 'end',
}

export interface ITooltipProps {
  /** Content to be rendered inside the tooltip */
  children: React.ReactNode;
  /** Indicates the position of the popout */
  placement?: PopoutPlacement;
  /** Indicates whether the Popover is opened */
  isOpen: boolean;
  /** Function to be executed when clicking outside the popover */
  onClickAway?: VoidFunction;
  /** Reference of the element from which the popover is going to be shown */
  reference: HTMLElement | null;
  /** id to reference the tooltip content by accessibility */
  id: string;
}

export const Tooltip: React.FC<WithCommonProps<ITooltipProps>> = ({
  children,
  placement = 'top',
  reference,
  onClickAway,
  isOpen,
  id,
  className: classNameProp,
  ...rest
}) => {
  const { setPopoutElement } = usePopout(reference, placement, {
    enabled: isOpen,
  });
  const clickAwayRef = React.useRef<HTMLElement | null>(null);
  useClickAway(clickAwayRef, () => {
    onClickAway?.();
  });

  const setRef = (element: HTMLDivElement) => {
    setPopoutElement(element);
    clickAwayRef.current = element;
  };

  const toolTipStylesAndSpacerClassName = twMerge(
    tooltipStylesAsClassName,
    classNameProp
  );
  return (
    <>
      {isOpen && (
        <>
          {createPortal(
            <div
              id={id}
              role="tooltip"
              ref={setRef}
              className={toolTipStylesAndSpacerClassName}
              {...rest}
            >
              <div className={tooltipContentStylesAsClassName}>{children}</div>
            </div>,
            window.document.body
          )}
        </>
      )}
    </>
  );
};
